import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSubscriptionPlans } from "../../../redux/Slices/PricingSlices/SubscriptionSlice";
import { FaStar, FaRocket, FaCrown } from "react-icons/fa";
import { AiOutlineCloud } from "react-icons/ai";
import Line from "../../../assets/line2.svg";

const PricingCards = () => {
  const [selectedDuration, setSelectedDuration] = useState("Monthly");
  const { subscriptionPlans, isLoading, loadingPlanId } = useSelector(
    (state) => state.subscriptions
  );

  const { user } = useSelector((state) => state?.auth);
  const dispatch = useDispatch();

  const iconMap = {
    Standard: <FaStar className="w-5 h-5" />,
    Scale: <FaRocket className="w-5 h-5" />,
    Ultimate: <FaCrown className="w-5 h-5" />,
    Default: <AiOutlineCloud className="w-5 h-5" />,
  };

  useEffect(() => {
    dispatch(fetchSubscriptionPlans());
  }, [dispatch]);

  const handleCheckboxChange = () => {
    setSelectedDuration((prev) => (prev === "Monthly" ? "Yearly" : "Monthly"));
  };

  const formatCredits = (credits) => {
    if (credits >= 1000) {
      const formatted = credits / 1000;
      return Number.isInteger(formatted)
        ? `${formatted}k`
        : `${formatted.toFixed(1)}k`;
    }
    return credits.toString();
  };

  const durationMapping = {
    Monthly: 30,
    Yearly: 365,
  };

  const filteredPlans = Array.isArray(subscriptionPlans) 
     ? subscriptionPlans.filter(
         (plan) => plan?.duration_days === durationMapping[selectedDuration]
       )
     : [];

  const handleSelectPlan = (planId) => {
    const baseUrl = process.env.REACT_APP_API_URL2;
    if (!baseUrl) {
        console.error("REACT_APP_PRICINGNEW_URL is not defined");
        return;
    }
    const url = `${baseUrl}/billing/checkout?plan=${planId}`;
    window.location.href = url;
  };

  const PricingCard = ({ plan }) => {
    return (
      <div
        className={`text-xl font-bold ${
          plan.name.toLowerCase().includes("standard")
            ? "bg-[#f7ede8]"
            : plan.name.toLowerCase().includes("scale")
            ? "bg-[#ebfaf2]"
            : plan.name.toLowerCase().includes("ultimate")
            ? "bg-[#f5f7fa]"
            : "bg-gradient-to-br from-blue-50 to-white"
        } relative flex flex-col items-start shadow-lg border-[1px] border-gray-400 rounded-xl py-6 px-5 hover:scale-105 transition-transform duration-300`}
        style={{ height: "390px", width: "300px" }}
      >
        <div className="flex items-center w-full">
          <div className="bg-blue-500 text-white p-2 rounded-full mb-1 shadow-md mr-2">
            {iconMap[plan.name] || iconMap.Default}
          </div>
          <h3
            className={`text-2xl font-bold ${
              plan.name.toLowerCase().includes("standard")
                ? "text-blue-600"
                : plan.name.toLowerCase().includes("scale")
                ? "text-purple-600"
                : plan.name.toLowerCase().includes("ultimate")
                ? "text-indigo-600"
                : "text-gray-600"
            }`}
          >
            {plan.name}
          </h3>
        </div>

        <p className="text-gray-700 text-sm mt-3">
          {plan.name.toLowerCase().includes("starter")
            ? "Essential features to quickly get you started."
            : plan.name.toLowerCase().includes("scale")
            ? "Advanced features designed for business growth."
            : plan.name.toLowerCase().includes("ultimate")
            ? "All premium features to ensure your success."
            : plan.name.toLowerCase().includes("standard")
            ? "A solid foundation with key essential features."
            : "Essential features to quickly get you started."}
        </p>

        <p className="text-gray-500 text-xl mt-2">
          {formatCredits(plan.credits)} Credits
        </p>

        <div className="mt-5 w-full">
          <div className="flex items-baseline">
            <p className="text-sm text-gray-500 font-semibold mr-2">
              Starting at
            </p>
            {plan.discount > 0 && (
              <p
                className="relative text-lg font-semibold text-gray-600"
                style={{
                  textDecoration: "none",
                  position: "relative",
                  display: "inline-block",
                }}
              >
                ${plan.price}
                <span
                  style={{
                    position: "absolute",
                    top: "48%",
                    left: "7%",
                    width: "100%",
                    height: "1px",
                    backgroundColor: "red",
                    transform: "rotate(-33deg)",
                    transformOrigin: "center",
                  }}
                ></span>
              </p>
            )}
          </div>
          <div className="flex items-baseline">
            <p className="text-5xl font-semibold text-gray-800 ml-0.5">
              ${plan.price}
            </p>
            <p className="text-base text-gray-500 ml-1">
              /{selectedDuration === "Monthly" ? "Monthly" : "Annually"}
            </p>
          </div>
          {plan.discount > 0 && (
            <>
              <p className="text-sm text-green-500 font-semibold mt-2">
                Enjoy {plan.discount}% off Due to {plan.discount_description}!
              </p>
            </>
          )}
          <p
            className={`${
              plan.discount > 0 ? "mt-0.5" : "mt-2"
            } text-lg font-semibold text-gray-800`}
          >
            Billed {selectedDuration === "Monthly" ? "Monthly" : "Annually"}
          </p>
        </div>

        <button
          onClick={() => handleSelectPlan(plan.id)}
          className="mt-auto mx-auto bg-blue-500 hover:bg-blue-600 text-white text-lg font-semibold py-3 px-8 rounded-full shadow-md transition-all duration-200"
          disabled={loadingPlanId === plan.id || isLoading}
        >
          {user ? "Select Plan" : "Start Trial"}
        </button>
      </div>
    );
  };

  return (
    <div className="bg-[#f4f8ff] !mt-8 sm:!mt-0 !pb-24 md:my-0 lg:pb-3 flex justify-center items-center">
      <div className="container px-4">
        <div className="pt-8 px-5 flex flex-col items-center">
          <div className="text-center w-full md:w-[80%] mb-8 lg:mt-12">
            <h2 className="text-3xl sm:text-4xl lg:text-[3rem] lg:leading-[3.5rem] font-bold">
              <span className="relative inline-block">
              Pricing Plans ,{" "}
                <img
                  src={Line}
                  alt="Underline decoration"
                  className="absolute bottom-0 left-0 w-full top-9 lg:top-[47px]"
                />
              </span>{" "}
              Verify and get precision
            </h2>
          </div>
        </div>
        <div className="flex justify-center items-center">
          <div className="pb-10">
            <label className="relative inline-flex cursor-pointer shadow-sm select-none items-center justify-center rounded-3xl bg-[#F7F9FA] p-1">
              <input
                type="checkbox"
                className="sr-only"
                checked={selectedDuration === "Yearly"}
                onChange={handleCheckboxChange}
              />
              <span
                className={`flex items-center font-semibold text-gray-400 rounded-3xl py-2 px-1 min-[1150px]:px-3 text-xs min-[1150px]:text-sm ${
                  selectedDuration === "Yearly"
                    ? "!text-black bg-white shadow-sm border"
                    : "text-gray-400"
                }`}
              >
                Yearly
                <span
                  className={`font-semibold text-white rounded-3xl py-0.5 px-2 text-sm ${
                    selectedDuration === "Yearly"
                      ? "!bg-[#0F8DF4]"
                      : "!bg-[#83C3F7] !text-[#F8FAFD]"
                  }`}
                  style={{ marginLeft: "8px" }}
                >
                  -30%
                </span>
              </span>
              <span
                className={`flex items-center text-gray-400 space-x-[6px] rounded-3xl py-2 min-[1150px]:px-3 px-1 text-xs min-[1150px]:text-sm font-semibold ${
                  selectedDuration === "Monthly"
                    ? "!text-black bg-white shadow-sm border"
                    : "text-gray-400"
                }`}
              >
                Monthly
              </span>
            </label>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 justify-center space-y-8 lg:space-y-0">
        {filteredPlans && filteredPlans.length > 0 ? (
             filteredPlans.map((plan) => (
               <div key={plan.id} className="flex justify-center md:mt-8 lg:mt-0">
                 <PricingCard plan={plan} />
               </div>
             ))
           ) : (
            <div className="col-span-full text-center text-gray-500">
              No pricing plans available for the selected duration.
              </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingCards;
